import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useMoodleCourses,
  useMoodleCategories,
  useMoodleTagAreas,
  useMoodleTagCollections,
  useMoodleTags,
} from './useMoodleHooks';
import { RootState } from '../support/store';
import {
  hydrateCategories,
  hydrateCourses,
  hydrateGallery,
  hydrateJoomlaCategories,
  hydrateJoomlaContent,
  hydrateLocations,
  hydrateSpeakers,
  hydrateSponsors,
  hydrateUsers,
  hydrateTagAreas,
  hydrateTagCollections,
  hydrateTags,
  hydrateJoomlaEventBookingCategories,
  setActiveMoodleInstance,
} from '../support/slices/moodleSlice';
import {
  useJoomlaCategories,
  useJoomlaContent,
  useJoomlaUserSearch,
  useMoodleGallery,
  useMoodleLocations,
  useMoodleSpeakers,
  useMoodleSponsors,
  useJoomlaEventBookingCategory,
} from './useMoodleEventBooking';
import {
  selectCategoriesArr,
  selectCoursesArr,
  selectGalleryArr,
  selectJoomlaCategoriesArr,
  selectJoomlaContentArr,
  selectJoomlaEventBookingCategoriesArr,
  selectLocationsArr,
  selectSpeakersArr,
  selectSponsorsArr,
  selectTagAreasArr,
  selectTagCollectionsArr,
  selectTagsArr,
  selectUsersArr,
} from '../support/moodleHelpers';
import { MoodleInstanceKey } from '../support/slices/moodleSlice';

interface Options {
  instance?: MoodleInstanceKey;
  activate?: boolean;
  enabled?: boolean;
}

const QUIET_BOOTSTRAP_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnMount: false as const,
  refetchOnReconnect: false,
  retry: 1,
};

export function useInitialMoodleData(options?: Options) {
  const instance = options?.instance ?? 'external';
  const activate = options?.activate ?? true;
  const enabled = options?.enabled ?? false;
  const dispatch = useDispatch();

  const reduxCourses = useSelector((state: RootState) => selectCoursesArr(state, instance));
  const reduxCategories = useSelector((state: RootState) => selectCategoriesArr(state, instance));
  const reduxUsers = useSelector((state: RootState) => selectUsersArr(state, instance));
  const reduxLocations = useSelector((state: RootState) => selectLocationsArr(state, instance));
  const reduxSpeakers = useSelector((state: RootState) => selectSpeakersArr(state, instance));
  const reduxSponsors = useSelector((state: RootState) => selectSponsorsArr(state, instance));
  const reduxGallery = useSelector((state: RootState) => selectGalleryArr(state, instance));
  const reduxJoomlaContent = useSelector((state: RootState) => selectJoomlaContentArr(state, instance));
  const reduxJoomlaCategories = useSelector((state: RootState) => selectJoomlaCategoriesArr(state, instance));
  const reduxTagAreas = useSelector((state: RootState) => selectTagAreasArr(state, instance));
  const reduxTagCollections = useSelector((state: RootState) => selectTagCollectionsArr(state, instance));
  const reduxTags = useSelector((state: RootState) => selectTagsArr(state, instance));
  const reduxJoomlaEventBookingCategories = useSelector((state: RootState) =>
    selectJoomlaEventBookingCategoriesArr(state, instance)
  );

  const { data: courses } = useMoodleCourses(
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxCourses.length === 0 },
    instance
  );

  const { data: users } = useJoomlaUserSearch(
    {},
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxUsers.length === 0 }
  );

  const { data: locations } = useMoodleLocations(
    1,
    1000,
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxLocations.length === 0 },
    instance
  );

  const { data: speakers } = useMoodleSpeakers(
    1,
    1000,
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxSpeakers.length === 0 },
    instance
  );

  const { data: sponsors } = useMoodleSponsors(
    1,
    1000,
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxSponsors.length === 0 },
    instance
  );

  const { data: gallery } = useMoodleGallery(
    1,
    1000,
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxGallery.length === 0 },
    instance
  );

  const { data: categories } = useMoodleCategories(
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxCategories.length === 0 },
    instance
  );

  const { data: joomlaContent } = useJoomlaContent(
    undefined,
    undefined,
    undefined,
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxJoomlaContent.length === 0 },
    instance
  );

  const { data: joomlaCategories } = useJoomlaCategories(
    undefined,
    undefined,
    { ...QUIET_BOOTSTRAP_QUERY_OPTIONS, enabled: enabled && reduxJoomlaCategories.length === 0 },
    instance
  );

  const { data: joomlaEventBookingCategories } = useJoomlaEventBookingCategory(
    1,
    1000,
    {
      ...QUIET_BOOTSTRAP_QUERY_OPTIONS,
      enabled: enabled && reduxJoomlaEventBookingCategories.length === 0,
    },
    instance
  );

  const { data: tagAreasData } = useMoodleTagAreas(instance, {
    ...QUIET_BOOTSTRAP_QUERY_OPTIONS,
    enabled: enabled && reduxTagAreas.length === 0,
  });

  const { data: tagCollectionsData } = useMoodleTagCollections(instance, {
    ...QUIET_BOOTSTRAP_QUERY_OPTIONS,
    enabled: enabled && reduxTagCollections.length === 0,
  });

  const { data: tagsData } = useMoodleTags(undefined, instance, {
    ...QUIET_BOOTSTRAP_QUERY_OPTIONS,
    enabled: enabled && reduxTags.length === 0,
  });

  useEffect(() => {
    if (enabled && activate) {
      dispatch(setActiveMoodleInstance(instance));
    }
  }, [enabled, activate, dispatch, instance]);

  useEffect(() => {
    if (courses?.length && reduxCourses.length === 0) {
      dispatch(hydrateCourses({ courses, instance }));
    }
  }, [courses, reduxCourses.length, dispatch, instance]);

  useEffect(() => {
    if (categories?.length && reduxCategories.length === 0) {
      dispatch(hydrateCategories({ categories, instance }));
    }
  }, [categories, reduxCategories.length, dispatch, instance]);

  useEffect(() => {
    if (joomlaContent?.items?.length && reduxJoomlaContent.length === 0) {
      dispatch(hydrateJoomlaContent({ content: joomlaContent.items, instance }));
    }
  }, [joomlaContent, reduxJoomlaContent.length, dispatch, instance]);

  useEffect(() => {
    if (joomlaCategories?.items?.length && reduxJoomlaCategories.length === 0) {
      dispatch(hydrateJoomlaCategories({ categories: joomlaCategories.items, instance }));
    }
  }, [joomlaCategories, reduxJoomlaCategories.length, dispatch, instance]);

  useEffect(() => {
    if (users?.length && reduxUsers.length === 0) {
      dispatch(hydrateUsers({ users, instance }));
    }
  }, [users, reduxUsers.length, dispatch, instance]);

  useEffect(() => {
    if (locations?.items?.length && reduxLocations.length === 0) {
      dispatch(hydrateLocations({ locations: locations.items, instance }));
    }
  }, [locations, reduxLocations.length, dispatch, instance]);

  useEffect(() => {
    if (speakers?.items?.length && reduxSpeakers.length === 0) {
      dispatch(hydrateSpeakers({ speakers: speakers.items, instance }));
    }
  }, [speakers, reduxSpeakers.length, dispatch, instance]);

  useEffect(() => {
    if (sponsors?.items?.length && reduxSponsors.length === 0) {
      dispatch(hydrateSponsors({ sponsors: sponsors.items, instance }));
    }
  }, [sponsors, reduxSponsors.length, dispatch, instance]);

  useEffect(() => {
    if (gallery?.items?.length && reduxGallery.length === 0) {
      dispatch(hydrateGallery({ gallery: gallery.items, instance }));
    }
  }, [gallery, reduxGallery.length, dispatch, instance]);

  useEffect(() => {
    if (
      joomlaEventBookingCategories?.items?.length &&
      reduxJoomlaEventBookingCategories.length === 0
    ) {
      dispatch(
        hydrateJoomlaEventBookingCategories({
          categories: joomlaEventBookingCategories.items,
          instance,
        })
      );
    }
  }, [joomlaEventBookingCategories, reduxJoomlaEventBookingCategories.length, dispatch, instance]);

  useEffect(() => {
    if (tagAreasData?.areas?.length && reduxTagAreas.length === 0) {
      dispatch(hydrateTagAreas({ areas: tagAreasData.areas, instance }));
    }
  }, [tagAreasData, reduxTagAreas.length, dispatch, instance]);

  useEffect(() => {
    if (tagCollectionsData?.collections?.length && reduxTagCollections.length === 0) {
      dispatch(
        hydrateTagCollections({ collections: tagCollectionsData.collections, instance })
      );
    }
  }, [tagCollectionsData, reduxTagCollections.length, dispatch, instance]);

  useEffect(() => {
    if (tagsData?.tags?.length && reduxTags.length === 0) {
      dispatch(hydrateTags({ tags: tagsData.tags, instance }));
    }
  }, [tagsData, reduxTags.length, dispatch, instance]);
}